import React, { useState, useEffect, FC, ReactElement } from 'react';
import { Link } from 'react-router-dom';
import i18next from 'i18next';
import { EventType, EventTypeRoute } from 'models/enums/EventType';
import { Box, Button } from '@material-ui/core';
import {
    IS_INFLUENCER_STREAMING_ENABLED,
    disabledStartStreaming,
    IS_VIEWER_STREAMING_ENABLED,
} from '../../../../environment/environment';
import { useStyles } from './styles';

interface BeginsInProps {
    eventDate: string;
    startIn: string;
    purchased: boolean;
    isCreator: boolean;
    eventType: { code: EventType | null; name: string | null };
    upcoming: boolean;
    live: boolean;
    eventUUID: string;
}

const EventStartTime: FC<BeginsInProps> = ({
    eventDate,
    startIn,
    purchased,
    isCreator,
    eventType,
    upcoming,
    live,
    eventUUID,
}) => {
    const classes = useStyles();
    const now = new Date().getTime();

    const [aboutToBegin, setAboutToBegin] = useState<boolean>(false);

    useEffect(() => {
        const now = new Date().getTime();

        if (live) {
            setAboutToBegin(false);
        } else if (eventDate && upcoming) {
            if (now > parseInt(eventDate, 10)) {
                setAboutToBegin(true);
            } else if (now < parseInt(eventDate, 10)) {
                setAboutToBegin(false);
            }
        }
    }, [eventDate, upcoming, live]);

    const getStreamingRoute = () => {
        if (eventType && eventType.code !== null) {
            if (eventType.code === EventType.WEBRTC) {
                return EventTypeRoute.WEBRTC_ROUTE;
            }
            return EventTypeRoute.RTMP_ROUTE;
        }

        return '';
    };

    const renderCreatorViewStreaming = (streamingRoute: string) => (
        <Box className={classes.streamingBox}>
            <Link className={classes.linkToStreaming} to={`/${streamingRoute}/${eventUUID}`}>
                <p className={classes.startText}>
                    {i18next.t('BeginInComponentStreaming')}{' '}
                    {live
                        ? i18next.t('BeginInComponentBegan')
                        : upcoming && i18next.t('BeginInComponentBegins')}{' '}
                    {startIn}{' '}
                </p>

                <Button className={classes.button}>{i18next.t('BeginInComponentStartStreaming')}</Button>
            </Link>
        </Box>
    );

    const renderAttendeeViewStreaming = (streamingRoute: string) => (
        <Box className={classes.streamingBox}>
            <Link className={classes.linkToStreaming} to={`/${streamingRoute}/${eventUUID}`}>
                {!aboutToBegin && (
                    <p className={classes.startText}>
                        {i18next.t('BeginInComponentStreaming')}{' '}
                        {live
                            ? i18next.t('BeginInComponentBegan')
                            : upcoming && i18next.t('BeginInComponentBegins')}{' '}
                        {startIn}{' '}
                    </p>
                )}

                {aboutToBegin && <p className={classes.startText}>The host hasn't started the event yet! </p>}

                {live && (
                    <Button className={classes.button}>{i18next.t('BeginInComponentViewStreaming')}</Button>
                )}
            </Link>
        </Box>
    );

    const renderViewStreaming = (): ReactElement | null => {
        const streamingRoute = getStreamingRoute();

        if (isCreator && IS_INFLUENCER_STREAMING_ENABLED) {
            return renderCreatorViewStreaming(streamingRoute);
        }
        if (purchased && IS_VIEWER_STREAMING_ENABLED) {
            return renderAttendeeViewStreaming(streamingRoute);
        }

        return null;
    };

    const renderUpcomingOrLiveStreaming = () => (
        <Box className={classes.streamingBox}>
            {!aboutToBegin && (
                <p className={classes.startText}>
                    {i18next.t('BeginInComponentStreaming')}{' '}
                    {live
                        ? i18next.t('BeginInComponentBegan')
                        : upcoming &&
                          now < parseInt(eventDate, 10) &&
                          i18next.t('BeginInComponentBegins')}{' '}
                    {startIn}
                </p>
            )}

            {aboutToBegin && <p className={classes.startText}>The host hasn&apos;t started the event yet!</p>}

            {(purchased || isCreator) && (
                <Button className={classes.button}>
                    {purchased
                        ? i18next.t('BeginInComponentViewStreaming')
                        : isCreator && i18next.t('BeginInComponentStartStreaming')}
                </Button>
            )}
        </Box>
    );

    if (disabledStartStreaming) return null;

    return (
        <>
            {(purchased || isCreator) && (upcoming || live)
                ? renderViewStreaming()
                : (upcoming || live) && renderUpcomingOrLiveStreaming()}
        </>
    );
};

export default EventStartTime;
